import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Gavel as DecisionIcon,
  Add as AddIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

import { decisionsApi, driversApi, projectsApi } from '../api/services';
import type { AnnualStrategicDriver, Decision, Project } from '../types/models';

// ---------------------------------------------------------------------------
// DecisionsPage — Phase 3 task 3.8.
//
// Lists every decision on the current user's account and exposes the new
// traceability fields (meeting source, evidence, outcome review date)
// alongside the existing title / rationale / stakeholders / driver / project.
// Each row opens an inline detail card; a "+" button and pencil icon open
// the same create/edit dialog so the page is a full decisions log surface.
// ---------------------------------------------------------------------------

type StatusFilter = 'All' | 'Draft' | 'Final' | 'UnderReview' | 'Revisited';

interface DecisionFormState {
  id: string | null;
  title: string;
  rationale: string;
  stakeholders: string;
  decisionDate: string;
  strategicDriverId: string;
  projectId: string;
  status: string;
  reviewDate: string;
  reviewReason: string;
  meetingSource: string;
  evidence: string;
  outcomeReviewDate: string;
}

const BLANK_FORM: DecisionFormState = {
  id: null,
  title: '',
  rationale: '',
  stakeholders: '',
  decisionDate: format(new Date(), 'yyyy-MM-dd'),
  strategicDriverId: '',
  projectId: '',
  status: 'Final',
  reviewDate: '',
  reviewReason: '',
  meetingSource: '',
  evidence: '',
  outcomeReviewDate: '',
};

function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  try { return format(new Date(iso), 'd MMM yyyy'); } catch { return iso; }
}

export default function DecisionsPage() {
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [drivers, setDrivers] = useState<AnnualStrategicDriver[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All');
  const [driverFilter, setDriverFilter] = useState<string>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<DecisionFormState>(BLANK_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [decs, drvs, prjs] = await Promise.all([
        decisionsApi.getAll(),
        driversApi.getAll().catch(() => [] as AnnualStrategicDriver[]),
        projectsApi.getAll().catch(() => [] as Project[]),
      ]);
      setDecisions(decs);
      setDrivers(drvs);
      setProjects(prjs);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not load decisions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return decisions.filter((d) => {
      if (statusFilter !== 'All' && d.status !== statusFilter) return false;
      if (driverFilter && d.strategicDriverId !== driverFilter) return false;
      return true;
    });
  }, [decisions, statusFilter, driverFilter]);

  const driverById = useMemo(() => {
    const m = new Map<string, AnnualStrategicDriver>();
    drivers.forEach((d) => m.set(d.id, d));
    return m;
  }, [drivers]);
  const projectById = useMemo(() => {
    const m = new Map<string, Project>();
    projects.forEach((p) => m.set(p.id, p));
    return m;
  }, [projects]);

  const handleOpenCreate = () => {
    setForm(BLANK_FORM);
    setDialogOpen(true);
  };

  const handleOpenEdit = (d: Decision) => {
    setForm({
      id: d.id,
      title: d.title,
      rationale: d.rationale ?? '',
      stakeholders: d.stakeholders ?? '',
      decisionDate: d.decisionDate.slice(0, 10),
      strategicDriverId: d.strategicDriverId ?? '',
      projectId: d.projectId ?? '',
      status: d.status,
      reviewDate: d.reviewDate?.slice(0, 10) ?? '',
      reviewReason: d.reviewReason ?? '',
      meetingSource: d.meetingSource ?? '',
      evidence: d.evidence ?? '',
      outcomeReviewDate: d.outcomeReviewDate?.slice(0, 10) ?? '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        title: form.title.trim(),
        rationale: form.rationale.trim() || undefined,
        stakeholders: form.stakeholders.trim() || undefined,
        decisionDate: form.decisionDate || undefined,
        strategicDriverId: form.strategicDriverId || undefined,
        projectId: form.projectId || undefined,
        status: form.status,
        reviewDate: form.reviewDate || undefined,
        reviewReason: form.reviewReason.trim() || undefined,
        meetingSource: form.meetingSource.trim() || undefined,
        evidence: form.evidence.trim() || undefined,
        outcomeReviewDate: form.outcomeReviewDate || undefined,
      };
      if (form.id) {
        await decisionsApi.update(form.id, payload);
      } else {
        await decisionsApi.create(payload);
      }
      setDialogOpen(false);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not save decision.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <DecisionIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h4">Decisions</Typography>
        </Stack>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Log decision
        </Button>
      </Stack>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
        Every decision recorded against this account, with its rationale, stakeholders, evidence, and review schedule.
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mb: 3 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFilter}
          onChange={(_, v) => v && setStatusFilter(v)}
        >
          {(['All', 'Draft', 'Final', 'UnderReview', 'Revisited'] as StatusFilter[]).map((s) => (
            <ToggleButton key={s} value={s}>{s === 'All' ? 'All' : s.replace(/([A-Z])/g, ' $1').trim()}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Select
          size="small"
          value={driverFilter}
          onChange={(e) => setDriverFilter(e.target.value)}
          displayEmpty
          sx={{ minWidth: 220 }}
        >
          <MenuItem value=""><em>All drivers</em></MenuItem>
          {drivers.map((d) => <MenuItem key={d.id} value={d.id}>{d.title}</MenuItem>)}
        </Select>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading && (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={28} /></Box>
      )}

      {!loading && filtered.length === 0 && (
        <Card><CardContent sx={{ textAlign: 'center', py: 6 }}>
          <DecisionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary">
            {decisions.length === 0 ? 'No decisions yet' : 'No decisions match your filters'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Use "Log decision" to capture a call you've made, with the meeting, stakeholders, and evidence that backed it.
          </Typography>
        </CardContent></Card>
      )}

      {!loading && filtered.length > 0 && (
        <Stack spacing={1.5}>
          {filtered.map((d) => {
            const driver = d.strategicDriverId ? driverById.get(d.strategicDriverId) : undefined;
            const project = d.projectId ? projectById.get(d.projectId) : undefined;
            const isExpanded = expandedId === d.id;
            return (
              <Card key={d.id} variant="outlined">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{d.title}</Typography>
                        <Chip size="small" label={d.status} />
                        <Chip size="small" label={formatDate(d.decisionDate)} variant="outlined" />
                        {d.isPending && <Chip size="small" label="Pending" color="warning" />}
                      </Stack>
                      <Stack direction="row" spacing={1.5} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
                        {driver && <Chip size="small" label={`Driver: ${driver.title}`} variant="outlined" />}
                        {project && <Chip size="small" label={`Project: ${project.title}`} variant="outlined" />}
                        {d.meetingSource && <Chip size="small" label={d.meetingSource} variant="outlined" />}
                      </Stack>
                      {isExpanded && (
                        <Stack spacing={1.5} sx={{ mt: 2 }}>
                          {d.rationale && <Meta label="Rationale" text={d.rationale} />}
                          {d.stakeholders && <Meta label="Stakeholders" text={d.stakeholders} />}
                          {d.evidence && <Meta label="Evidence" text={d.evidence} />}
                          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                            <Meta label="Review date" text={formatDate(d.reviewDate)} />
                            <Meta label="Outcome review" text={formatDate(d.outcomeReviewDate)} />
                            {d.reviewReason && <Meta label="Review reason" text={d.reviewReason} />}
                          </Stack>
                          {d.resolution && (
                            <Meta
                              label={`Resolution (${formatDate(d.resolvedAt)})`}
                              text={d.resolutionNotes ? `${d.resolution} — ${d.resolutionNotes}` : d.resolution}
                            />
                          )}
                        </Stack>
                      )}
                    </Box>
                    <Stack spacing={0.5} alignItems="flex-end">
                      <IconButton size="small" onClick={() => handleOpenEdit(d)} aria-label="Edit decision">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setExpandedId(isExpanded ? null : d.id)}
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={saving ? undefined : () => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{form.id ? 'Edit decision' : 'Log decision'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="Title"
              fullWidth
              required
              size="small"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <TextField
              label="Rationale"
              fullWidth
              multiline
              minRows={2}
              size="small"
              value={form.rationale}
              onChange={(e) => setForm({ ...form, rationale: e.target.value })}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Decision date"
                type="date"
                size="small"
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
                value={form.decisionDate}
                onChange={(e) => setForm({ ...form, decisionDate: e.target.value })}
              />
              <Select
                size="small"
                fullWidth
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                {['Draft', 'Final', 'UnderReview', 'Revisited'].map((s) => (
                  <MenuItem key={s} value={s}>{s.replace(/([A-Z])/g, ' $1').trim()}</MenuItem>
                ))}
              </Select>
            </Stack>
            <TextField
              label="Stakeholders"
              fullWidth
              size="small"
              placeholder="Comma-separated names"
              value={form.stakeholders}
              onChange={(e) => setForm({ ...form, stakeholders: e.target.value })}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={form.strategicDriverId}
                onChange={(e) => setForm({ ...form, strategicDriverId: e.target.value })}
              >
                <MenuItem value=""><em>No driver</em></MenuItem>
                {drivers.map((d) => <MenuItem key={d.id} value={d.id}>{d.title}</MenuItem>)}
              </Select>
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={form.projectId}
                onChange={(e) => setForm({ ...form, projectId: e.target.value })}
              >
                <MenuItem value=""><em>No project</em></MenuItem>
                {projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.title}</MenuItem>)}
              </Select>
            </Stack>
            <TextField
              label="Meeting source"
              fullWidth
              size="small"
              placeholder="e.g. Q1 Leadership Sync, 18 Apr"
              value={form.meetingSource}
              onChange={(e) => setForm({ ...form, meetingSource: e.target.value })}
            />
            <TextField
              label="Evidence"
              fullWidth
              multiline
              minRows={3}
              size="small"
              placeholder="Links, documents, data points, or quotes — one per line."
              value={form.evidence}
              onChange={(e) => setForm({ ...form, evidence: e.target.value })}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Review date"
                type="date"
                size="small"
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
                value={form.reviewDate}
                onChange={(e) => setForm({ ...form, reviewDate: e.target.value })}
                helperText="When to reconsider the decision itself"
              />
              <TextField
                label="Outcome review"
                type="date"
                size="small"
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
                value={form.outcomeReviewDate}
                onChange={(e) => setForm({ ...form, outcomeReviewDate: e.target.value })}
                helperText="When to check whether it was right"
              />
            </Stack>
            <TextField
              label="Review reason"
              fullWidth
              size="small"
              value={form.reviewReason}
              onChange={(e) => setForm({ ...form, reviewReason: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.title.trim()}
          >
            {saving ? 'Saving…' : form.id ? 'Save changes' : 'Log decision'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Meta({ label, text }: { label: string; text: string }) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600, mb: 0.25 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {text}
      </Typography>
    </Box>
  );
}
